import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  ITableWithInputColumnFormModel,
  ITableWithInputColumnValidationMessage,
  ITableWithInputConfigurationFormModel,
  ITableWithInputUniqueFieldsCombinationFormModel,
} from '../../../models/form-builder-types-configurations.model';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import {
  EIremboFormlyFieldTypes,
  TableWithInputColumn,
} from '@irembo-andela/irembogov3-common';
import { getDefaultBaseSettingsConfigFormly } from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  ADVANCED_FORM_FIELD_TYPES,
  definitions,
} from './form-field-formly-configuration-classes/advanced-formly-field-configuration';
import { nidFieldGetConfigurationConfig } from './nid-field-type.configuration';
import { tinInputGetConfigurationConfig } from './custom-tin-input.configuration';
import { crvsInputGetConfigurationConfig } from './crvs-id-input.configuration';
import { doubleIdInputGetConfigurationConfig } from './double-id-field-type.configurator';
import { currencyFormatInputFieldGetConfigurationConfig } from './custom-currency-format-field-type.configuration';
import { internationalPhoneNumberFieldGetConfigurationConfig } from './international-phone-number-field-type.configuration';
import { multiCheckboxFieldGetConfigurationConfig } from './multicheckbox-field-type.configuration';
import { otpDataFetchInputGetConfigurationConfig } from './custom-otp-data-fetch-field-type.configuration';
import { genericDataFetchInputGetConfigurationConfig } from './generic-datafetch-field-type.configuration';
import { sectionMessageWithLinkGetConfigurationConfig } from './custom-section-with-message.configuration';
import { customDynamicRepeaterFieldGetConfigurationConfig } from './custom-dynamic-repeater-field-type.configuration';
import { applicationNumberGetConfigurationConfig } from './application-number-field-type.configuration';
import { slotbookingGetConfigurationConfig } from './custom-slot-booking.configuration';
import { dateTimePickerFieldGetConfiguration } from './custom-datetimepicker-field.configuration';
import { timePickerFieldGetConfiguration } from './custom-timepicker-field-type.configuration';
import { setRequiredValidatorUpdateFieldFromModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { parseRequiredValidatorParseFieldIntoModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';

export const defaultTableWithInputConfigFormModel: ITableWithInputConfigurationFormModel =
  {
    fieldLabel: null,
    placeholder: null,
    hint: null,
    tooltip: null,
    internalDescription: null,
    columns: [],
    minItems: 1,
    maxItems: null,
    enableEdit: true,
    enableDeletion: true,
    enableRowSelection: false,
    showSearch: false,
    showPagination: true,
    itemsPerPage: 10,
    uniqueFieldsCombination: {
      enabled: false,
      firstField: null,
      secondField: null,
      message: null,
    },
  };

// Options for the composite-uniqueness field pickers below: the table's own
// currently-configured columns (label, key), read straight off the settings
// form's reactive control tree rather than the formly model, so it stays
// current as columns are added/renamed/removed without wiring a separate
// subscription — matching how sibling settings already reach across the
// form (e.g. the columns-driven repeater rebuild in
// data-table-field-type.configurator.ts's `onInit` hook).
const getColumnFieldOptions = (
  field: FormlyFieldConfig
): { label: string; value: string }[] => {
  const columns: ITableWithInputColumnFormModel[] =
    field.formControl?.root?.get?.('columns')?.value ?? [];
  return columns
    .filter(column => !!column?.key)
    .map(column => ({ label: column.label || column.key, value: column.key }));
};

type ColumnFieldType = ITableWithInputColumnFormModel['type'];

// What a column may be: every field type ui-common registers, except the
// ones that make no sense as a single cell — the tables (a table cannot be
// nested in a table cell, whichever kind), the pure layout containers, which
// hold other fields rather than a value of their own, and the whole-service
// and selfie-verification widgets, which are flows rather than values. Built
// from the enum so a type added to ui-common shows up here without a
// matching edit; the label map only supplies the wording shown to the SM.
const EXCLUDED_COLUMN_FIELD_TYPES: ReadonlySet<string> = new Set([
  EIremboFormlyFieldTypes.customtablewithinputform,
  EIremboFormlyFieldTypes.customdatatable,
  EIremboFormlyFieldTypes.customeditabletable,
  EIremboFormlyFieldTypes.customdatasettable,
  EIremboFormlyFieldTypes.customitemselectiontable,
  EIremboFormlyFieldTypes['formly-group'],
  EIremboFormlyFieldTypes.sections,
  EIremboFormlyFieldTypes.block,
  EIremboFormlyFieldTypes.customsingleservicerender,
  EIremboFormlyFieldTypes.customserviceviewer,
  EIremboFormlyFieldTypes.customselfieverification,
]);

const COLUMN_FIELD_TYPE_LABELS: Partial<Record<ColumnFieldType, string>> = {
  input: 'Text',
  number: 'Number',
  email: 'Email',
  textarea: 'Text Area',
  select: 'Select',
  radio: 'Radio Input',
  checkbox: 'Checkbox',
  multicheckbox: 'Multi-checkbox',
  customdatepicker: 'Date',
  customdatetimepicker: 'Date Time Picker',
  customtimepicker: 'Time Picker',
  customdropdown: 'Dropdown Selector',
  customdropdownpaginated: 'Dropdown Selector With Dataset Paginated',
  customdropdowndatafetchpaginated: 'Paginated Data Fetch Dropdown',
  custommultiselectdatafetch: 'Multi-select Data Fetch',
  customcascadingdropdowns: 'Cascading Dropdown',
  customphonenumber: 'International Phone No.',
  customidinput: 'NID',
  customdoubleidinput: 'Double ID Input',
  customcrvsidinput: 'CRVS Data Fetch',
  customtininput: 'TIN',
  customapplicationnumberinput: 'Application Number',
  customcurrencyformatinput: 'Currency Format Input',
  customeditableinput: 'Editable Input',
  customcascadingeditableinput: 'Cascading Editable Input',
  customfileupload: 'Attachment',
  custominternalfileupload: 'Internal File Upload',
  externalfileupload: 'External File Upload',
  customimage: 'Image',
  customparagraph: 'Paragraph',
  customsectionmessagewithlink: 'Section Message With Link',
  customgenericdatafetch: 'Generic Data Fetch',
  customconditionaldatafetch: 'Conditional Data Fetch',
  customotpdatafetch: 'OTP Data Fetch',
  customdirectdatafetch: 'Direct Data Fetch',
  customgenericguardeddatafetch: 'Guarded Data Fetch',
  genericbuttonfetch: 'Fetch Button',
  customslotbooking: 'Slot Booking',
  customrepeater: 'Repeatable Input',
  customdynamicrepeater: 'Dynamic Repeatable Input',
  customexpansionrepeater: 'Expansion Repeater',
  customlabelvaluerepeater: 'Label and Value Repeater',
  customfieldtabs: 'Field Tabs',
  customerrorcomponent: 'Error Component',
};

// The plain-input variants first, then everything else alphabetically by
// label, so the dropdown still opens on the few types most columns use.
const LEADING_COLUMN_FIELD_TYPES: ColumnFieldType[] = [
  'input',
  'number',
  'email',
  'textarea',
  'customdatepicker',
  'customdropdown',
  'checkbox',
];

const columnFieldTypeLabel = (type: ColumnFieldType): string =>
  COLUMN_FIELD_TYPE_LABELS[type] ?? type;

export const getColumnFieldTypeOptions = (): {
  label: string;
  value: ColumnFieldType;
}[] => {
  const registered = (
    Object.values(EIremboFormlyFieldTypes) as ColumnFieldType[]
  ).filter(type => !EXCLUDED_COLUMN_FIELD_TYPES.has(type));
  const remaining = registered
    .filter(type => !LEADING_COLUMN_FIELD_TYPES.includes(type))
    .sort((a, b) =>
      columnFieldTypeLabel(a).localeCompare(columnFieldTypeLabel(b))
    );
  return [...LEADING_COLUMN_FIELD_TYPES, ...remaining].map(type => ({
    label: columnFieldTypeLabel(type),
    value: type,
  }));
};

// Column types whose value is picked from configured options, so the options
// editor applies to them; `multiple` only makes sense for the two that can
// hold several picks.
const OPTION_BACKED_COLUMN_FIELD_TYPES: ReadonlySet<string> = new Set([
  EIremboFormlyFieldTypes.customdropdown,
  EIremboFormlyFieldTypes.select,
  EIremboFormlyFieldTypes.radio,
  EIremboFormlyFieldTypes.multicheckbox,
]);

const MULTIPLE_CAPABLE_COLUMN_FIELD_TYPES: ReadonlySet<string> = new Set([
  EIremboFormlyFieldTypes.customdropdown,
  EIremboFormlyFieldTypes.select,
]);

// Type-specific column settings below. Each group names the column types whose
// rendered field actually reads those props, so a setting only appears where it
// does something. The runtime table forwards a column's whole `props` object to
// the rendered field (see buildRowField in custom-table-with-input.component),
// so every prop written here reaches the same component the standalone field
// would use, under the same name.
const DATE_COLUMN_FIELD_TYPES: ReadonlySet<string> = new Set([
  EIremboFormlyFieldTypes.customdatepicker,
  EIremboFormlyFieldTypes.customdatetimepicker,
  EIremboFormlyFieldTypes.customtimepicker,
]);

// Text-ish inputs: length and pattern constraints apply, numeric range doesn't.
const TEXT_LENGTH_COLUMN_FIELD_TYPES: ReadonlySet<string> = new Set([
  'input',
  'email',
  EIremboFormlyFieldTypes.textarea,
]);

// Only the upload type that has no definition in ADVANCED_FORM_FIELD_TYPES —
// the internal and external variants bring their own, richer settings from
// there (see getAdvancedColumnSettings).
const FILE_UPLOAD_COLUMN_FIELD_TYPES: ReadonlySet<string> = new Set([
  EIremboFormlyFieldTypes.customfileupload,
]);

// Dropdowns backed by a dataset/endpoint rather than a static options list.
// The paginated data-fetch and multi-select variants carry their own dataset
// settings from ADVANCED_FORM_FIELD_TYPES, so only the plain paginated one
// needs the generic dataset fields below.
const DATASET_DROPDOWN_COLUMN_FIELD_TYPES: ReadonlySet<string> = new Set([
  EIremboFormlyFieldTypes.customdropdownpaginated,
]);

// bindLabel/bindValue apply to every dropdown, dataset-backed or not.
const BIND_CAPABLE_COLUMN_FIELD_TYPES: ReadonlySet<string> = new Set([
  ...OPTION_BACKED_COLUMN_FIELD_TYPES,
  ...DATASET_DROPDOWN_COLUMN_FIELD_TYPES,
]);

// The type of the column a settings field belongs to. Settings sit at
// different depths — a hand-written one directly under the column, a reused
// panel inside that type's branch and often a group within it — so this walks
// up to the nearest model that is a column instead of assuming the column is
// the immediate parent.
//
// A column is recognised by its `type` alone: that is the one setting every
// column has from the moment it is added (it defaults to Text), whereas the
// key and label only exist once the SM has typed them. Requiring the key as
// well hid every type-specific setting on a freshly added column until a key
// was entered — picking Date, NID, etc. appeared to do nothing. A reused
// panel's own branch never carries a `type` of its own (see
// COLUMN_OWNED_PROP_KEYS), so the walk cannot stop at one by mistake.
const columnOf = (
  field: FormlyFieldConfig
): Partial<ITableWithInputColumnFormModel> | undefined => {
  for (let current = field.parent; current; current = current.parent) {
    const model = current.model as Record<string, unknown> | undefined;
    if (model && typeof model === 'object' && 'type' in model) {
      return model as Partial<ITableWithInputColumnFormModel>;
    }
  }
  return undefined;
};

const columnTypeOf = (field: FormlyFieldConfig): string | undefined =>
  columnOf(field)?.type;

const hideUnlessType =
  (types: ReadonlySet<string>) =>
  (field: FormlyFieldConfig): boolean =>
    !types.has(columnTypeOf(field) ?? '');

// Written into column.props verbatim by the mappers below, so the form model
// key and the prop name are deliberately identical.
const TYPE_SPECIFIC_COLUMN_PROP_KEYS = [
  'minDate',
  'maxDate',
  'dateFormat',
  'delimeter',
  'allowedDaysOfTheWeek',
  'min',
  'max',
  'minLength',
  'maxLength',
  'pattern',
  'rows',
  'allowedFormats',
  'minimumUploadSize',
  'maximumUploadSize',
  'attachmentCode',
  'dataset',
  'useBaseUrl',
  'virtualScroll',
  'bindLabel',
  'bindValue',
] as const;

// Every type in ADVANCED_FORM_FIELD_TYPES already describes its own settings
// (the panel a standalone field of that type gets) as a definition of
// `propKeys` + `fields`. Reuse those verbatim for a column of that type rather
// than maintaining a second copy here.
//
// Each type's fields go under a group keyed by the type itself, NOT at the
// column's own level: the definitions overlap heavily (`populates` appears in
// 8 of them, `useBaseUrl` in 8, `endpointCode` in 8), and several of those
// names are also used by the hand-written settings above. As siblings they
// would be many fields competing for one control per name, which builds no
// panel at all. Keyed per type, each definition gets its own branch of the
// column's form model and nothing collides. The mappers below flatten the
// active type's branch into column.props and back.
const advancedColumnTypes = (): EIremboFormlyFieldTypes[] =>
  ADVANCED_FORM_FIELD_TYPES.filter(
    type => !EXCLUDED_COLUMN_FIELD_TYPES.has(type)
  );

const getAdvancedColumnSettings = (
  formBuilderService: FormBuilderService
): FormlyFieldConfig[] => {
  const byType = definitions(formBuilderService);
  return advancedColumnTypes()
    .filter(type => byType[type])
    .map(type => ({
      key: type,
      fieldGroup: stripOwnFormBehaviour(byType[type]?.fields ?? []),
      expressions: {
        hide: (field: FormlyFieldConfig) => columnTypeOf(field) !== type,
      },
    }));
};

// Types whose settings live in a bespoke configuration rather than in
// ADVANCED_FORM_FIELD_TYPES. Each of these exports the same shape — a
// configuration whose `settings.formlyForm` is the base settings followed by
// that type's own — so a column of such a type can show the second half.
const BESPOKE_COLUMN_TYPE_CONFIGS: Partial<
  Record<
    string,
    (service: FormBuilderService) => IFormbuilderFieldTypeConfiguration
  >
> = {
  [EIremboFormlyFieldTypes.customidinput]: nidFieldGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customtininput]: tinInputGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customcrvsidinput]: crvsInputGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customdoubleidinput]:
    doubleIdInputGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customcurrencyformatinput]:
    currencyFormatInputFieldGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customphonenumber]:
    internationalPhoneNumberFieldGetConfigurationConfig,
  [EIremboFormlyFieldTypes.multicheckbox]:
    multiCheckboxFieldGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customotpdatafetch]:
    otpDataFetchInputGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customgenericdatafetch]:
    genericDataFetchInputGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customsectionmessagewithlink]:
    sectionMessageWithLinkGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customdynamicrepeater]:
    customDynamicRepeaterFieldGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customapplicationnumberinput]:
    applicationNumberGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customslotbooking]:
    slotbookingGetConfigurationConfig,
  [EIremboFormlyFieldTypes.customdatetimepicker]:
    dateTimePickerFieldGetConfiguration,
  [EIremboFormlyFieldTypes.customtimepicker]: timePickerFieldGetConfiguration,
};

// What every field's settings panel starts with, and what a column already
// asks for in its own right — dropped so a column doesn't offer a second
// label, key or hint.
const BASE_SETTINGS_KEYS: ReadonlySet<string> = new Set([
  'label',
  'fieldKey',
  'placeholder',
  'hint',
  'hintPosition',
  'tooltip',
  'internalDescription',
  'readonly',
  'value',
  'multiple',
  'contentSection',
  'optionLabelValuePairs',
]);

// What is being reused from another type's settings panel is the fields —
// not the behaviour wired around them. Its hooks reach for siblings by key,
// and its group validators read the model shape that panel has: the date
// picker's, for one, reads control.value.addRemoveTimeMinDateSetting.offSet.
// A column has neither, and either one throwing takes the whole panel's
// build down with it — which is how picking Text showed every other type's
// settings, no hide expression having run at all.
const withoutOwnFormBehaviour = (
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  const copy = { ...field };
  delete copy.hooks;
  delete copy.validators;
  delete copy.asyncValidators;
  if (copy.fieldGroup) {
    copy.fieldGroup = stripOwnFormBehaviour(copy.fieldGroup);
  }
  return copy;
};

const stripOwnFormBehaviour = (
  fields: FormlyFieldConfig[]
): FormlyFieldConfig[] => fields.map(withoutOwnFormBehaviour);

// A settings panel is base settings followed by the type's own; keep the
// latter. Fields the base already provides are dropped by key, and a group
// left empty by that is dropped with them.
const stripBaseSettingsFields = (
  fields: FormlyFieldConfig[]
): FormlyFieldConfig[] =>
  fields
    .filter(field => {
      if (typeof field.key === 'string') {
        return !BASE_SETTINGS_KEYS.has(field.key);
      }
      if (field.fieldGroup) {
        field.fieldGroup = stripBaseSettingsFields(field.fieldGroup);
        return field.fieldGroup.length > 0;
      }
      return true;
    })
    .map(withoutOwnFormBehaviour);

const collectFieldKeys = (fields: FormlyFieldConfig[]): string[] =>
  fields.flatMap(field =>
    typeof field.key === 'string'
      ? [field.key]
      : collectFieldKeys(field.fieldGroup ?? [])
  );

// Built per type, and defensively: a configuration that throws while building
// must cost only its own settings, never the whole column panel.
const buildBespokeColumnSettings = (
  formBuilderService: FormBuilderService
): { type: string; fields: FormlyFieldConfig[]; propKeys: string[] }[] =>
  Object.entries(BESPOKE_COLUMN_TYPE_CONFIGS)
    .map(([type, getConfig]) => {
      try {
        const fields = stripBaseSettingsFields(
          getConfig?.(formBuilderService).settings.formlyForm ?? []
        );
        return { type, fields, propKeys: collectFieldKeys(fields) };
      } catch (error) {
        console.error(`Could not build column settings for ${type}:`, error);
        return { type, fields: [], propKeys: [] };
      }
    })
    .filter(entry => entry.fields.length > 0);

const getBespokeColumnSettings = (
  formBuilderService: FormBuilderService
): FormlyFieldConfig[] =>
  buildBespokeColumnSettings(formBuilderService).map(({ type, fields }) => ({
    key: type,
    fieldGroup: fields,
    expressions: {
      hide: (field: FormlyFieldConfig) => columnTypeOf(field) !== type,
    },
  }));

// What the column itself owns, so the mappers never mistake one of these for a
// type-specific setting when moving values in and out of a branch.
const COLUMN_OWNED_PROP_KEYS: ReadonlySet<string> = new Set([
  'label',
  'required',
  'unique',
  'hiddenColumn',
  'widthPercent',
  'multiple',
  'options',
  'type',
]);

// Error names the row panel commonly raises, offered only as a hint on the
// free-text field below. The list is deliberately not a closed union: a
// column can raise an error from any validator attached to its field type,
// including ones added after this wizard was written, and every one of them
// has to be nameable here without a code change.
const COMMON_COLUMN_ERROR_NAMES = [
  'email',
  'pattern',
  'minlength',
  'maxlength',
  'min',
  'max',
] as const;

// A single repeating "error name -> message" row, rendered once per entry.
// Every message is written through the one `errorMessage` key; which error it
// belongs to is the sibling value, not the key, so the number of errors a
// column can carry wording for is unbounded.
const columnValidationMessagesField = (): FormlyFieldConfig => ({
  key: 'validationMessages',
  type: EIremboFormlyFieldTypes.customrepeater,
  props: {
    hideHeader: true,
    required: false,
    addText: 'Add Error Message',
    dragDisabled: true,
    title: 'Error Message',
  },
  fieldArray: {
    type: EIremboFormlyFieldTypes['formly-group'],
    props: { hideHeader: true, dragDisabled: true },
    fieldGroup: [
      {
        key: 'errorName',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Validation Rule',
          required: true,
          description: `The error this message is for, e.g. ${COMMON_COLUMN_ERROR_NAMES.join(
            ', '
          )}`,
        },
      },
      {
        key: 'errorMessage',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Error Message',
          required: true,
          description: 'Shown under the field when this rule is not met',
        },
      },
    ],
  },
});

const getTypeSpecificColumnSettings = (): FormlyFieldConfig[] => [
  {
    key: 'minDate',
    type: EIremboFormlyFieldTypes.customdatepicker,
    props: { label: 'Minimum Date' },
    expressions: { hide: hideUnlessType(DATE_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'maxDate',
    type: EIremboFormlyFieldTypes.customdatepicker,
    props: { label: 'Maximum Date' },
    expressions: { hide: hideUnlessType(DATE_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'dateFormat',
    type: EIremboFormlyFieldTypes.input,
    props: { label: 'Date Format', description: 'e.g., DD MM YYYY' },
    expressions: { hide: hideUnlessType(DATE_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'delimeter',
    type: EIremboFormlyFieldTypes.input,
    props: { label: 'Date Delimiter', description: 'e.g., / or -' },
    expressions: { hide: hideUnlessType(DATE_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'allowedDaysOfTheWeek',
    type: EIremboFormlyFieldTypes.multicheckbox,
    props: {
      label: 'Allowed Days of Week',
      options: [
        { label: 'Sunday', value: 0 },
        { label: 'Monday', value: 1 },
        { label: 'Tuesday', value: 2 },
        { label: 'Wednesday', value: 3 },
        { label: 'Thursday', value: 4 },
        { label: 'Friday', value: 5 },
        { label: 'Saturday', value: 6 },
      ],
    },
    expressions: { hide: hideUnlessType(DATE_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'min',
    type: EIremboFormlyFieldTypes.input,
    props: { type: 'number', label: 'Minimum Value' },
    expressions: {
      hide: (field: FormlyFieldConfig) => columnTypeOf(field) !== 'number',
    },
  },
  {
    key: 'max',
    type: EIremboFormlyFieldTypes.input,
    props: { type: 'number', label: 'Maximum Value' },
    expressions: {
      hide: (field: FormlyFieldConfig) => columnTypeOf(field) !== 'number',
    },
  },
  {
    key: 'minLength',
    type: EIremboFormlyFieldTypes.input,
    props: { type: 'number', label: 'Minimum Length', min: 0 },
    expressions: { hide: hideUnlessType(TEXT_LENGTH_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'maxLength',
    type: EIremboFormlyFieldTypes.input,
    props: { type: 'number', label: 'Maximum Length', min: 1 },
    expressions: { hide: hideUnlessType(TEXT_LENGTH_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'pattern',
    type: EIremboFormlyFieldTypes.input,
    props: {
      label: 'Pattern',
      description: 'Regular expression the value must match',
    },
    expressions: { hide: hideUnlessType(TEXT_LENGTH_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'rows',
    type: EIremboFormlyFieldTypes.input,
    props: { type: 'number', label: 'Rows', min: 1 },
    expressions: {
      hide: (field: FormlyFieldConfig) =>
        columnTypeOf(field) !== EIremboFormlyFieldTypes.textarea,
    },
  },
  {
    key: 'allowedFormats',
    type: EIremboFormlyFieldTypes.multicheckbox,
    props: {
      label: 'Allowed File Formats',
      options: [
        { label: 'PDF', value: 'pdf' },
        { label: 'JPG', value: 'jpg' },
        { label: 'JPEG', value: 'jpeg' },
        { label: 'PNG', value: 'png' },
        { label: 'DOC', value: 'doc' },
        { label: 'DOCX', value: 'docx' },
        { label: 'XLS', value: 'xls' },
        { label: 'XLSX', value: 'xlsx' },
      ],
    },
    expressions: { hide: hideUnlessType(FILE_UPLOAD_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'minimumUploadSize',
    type: EIremboFormlyFieldTypes.input,
    props: { type: 'number', label: 'Minimum Upload Size (KB)', min: 0 },
    expressions: { hide: hideUnlessType(FILE_UPLOAD_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'maximumUploadSize',
    type: EIremboFormlyFieldTypes.input,
    props: { type: 'number', label: 'Maximum Upload Size (KB)', min: 1 },
    expressions: { hide: hideUnlessType(FILE_UPLOAD_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'attachmentCode',
    type: EIremboFormlyFieldTypes.input,
    props: { label: 'Attachment Code' },
    expressions: { hide: hideUnlessType(FILE_UPLOAD_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'dataset',
    type: EIremboFormlyFieldTypes.input,
    props: {
      label: 'Dataset',
      description: 'Dataset or endpoint code the options are fetched from',
    },
    expressions: { hide: hideUnlessType(DATASET_DROPDOWN_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'useBaseUrl',
    type: EIremboFormlyFieldTypes.checkbox,
    defaultValue: false,
    props: { label: 'Use Base URL', formCheck: 'switch' },
    expressions: { hide: hideUnlessType(DATASET_DROPDOWN_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'virtualScroll',
    type: EIremboFormlyFieldTypes.checkbox,
    defaultValue: false,
    props: { label: 'Virtual Scroll', formCheck: 'switch' },
    expressions: { hide: hideUnlessType(DATASET_DROPDOWN_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'bindLabel',
    type: EIremboFormlyFieldTypes.input,
    props: {
      label: 'Option Label Property',
      description: "Defaults to 'label'",
    },
    expressions: { hide: hideUnlessType(BIND_CAPABLE_COLUMN_FIELD_TYPES) },
  },
  {
    key: 'bindValue',
    type: EIremboFormlyFieldTypes.input,
    props: {
      label: 'Option Value Property',
      description: "Defaults to 'value'",
    },
    expressions: { hide: hideUnlessType(BIND_CAPABLE_COLUMN_FIELD_TYPES) },
  },
];

export const tableWithInputFieldGetConfigurationConfig = (
  formBuilderService: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultTableWithInputConfigFormModel,
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderService),
        {
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Table Configuration',
          },
          fieldGroup: [
            {
              key: 'columns',
              type: EIremboFormlyFieldTypes.customexpansionrepeater,
              props: {
                label: 'Field',
                addText: 'Add Field',
                hideRepeaterLabel: true,
                required: false,
                titleFieldName: 'label',
              },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'label',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Column Label',
                      required: true,
                    },
                  },
                  {
                    key: 'key',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Column Key',
                      required: true,
                      description: 'Unique key this value is stored under',
                    },
                  },
                  {
                    key: 'type',
                    type: EIremboFormlyFieldTypes.customdropdown,
                    defaultValue: 'input',
                    props: {
                      label: 'Field Type',
                      required: true,
                      bindLabel: 'label',
                      bindValue: 'value',
                      options: getColumnFieldTypeOptions(),
                    },
                  },
                  {
                    key: 'required',
                    type: EIremboFormlyFieldTypes.checkbox,
                    defaultValue: false,
                    props: {
                      label: 'Required',
                      formCheck: 'switch',
                    },
                  },
                  {
                    key: 'errorMessage',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Error Message',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) =>
                        !columnOf(field)?.required,
                    },
                  },
                  {
                    key: 'unique',
                    type: EIremboFormlyFieldTypes.checkbox,
                    defaultValue: false,
                    props: {
                      label: 'Must Be Unique Across Rows',
                      formCheck: 'switch',
                    },
                  },
                  {
                    key: 'uniquenessErrorMessage',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Uniqueness Error Message',
                      description: 'e.g., This value already exists',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) =>
                        !columnOf(field)?.unique,
                    },
                  },
                  columnValidationMessagesField(),
                  {
                    key: 'hiddenColumn',
                    type: EIremboFormlyFieldTypes.checkbox,
                    defaultValue: false,
                    props: {
                      label: 'Hide Column In Table',
                      formCheck: 'switch',
                    },
                  },
                  {
                    key: 'widthPercent',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      type: 'number',
                      label: 'Column Width (%)',
                      min: 10,
                      max: 100,
                    },
                  },
                  {
                    key: 'multiple',
                    type: EIremboFormlyFieldTypes.checkbox,
                    defaultValue: false,
                    props: {
                      label: 'Allow Multiple Selection',
                      formCheck: 'switch',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) =>
                        !MULTIPLE_CAPABLE_COLUMN_FIELD_TYPES.has(
                          columnTypeOf(field) ?? ''
                        ),
                    },
                  },
                  {
                    key: 'options',
                    type: EIremboFormlyFieldTypes.customexpansionrepeater,
                    wrappers: ['custom-expansion-panel-with-icon'],
                    props: {
                      label: 'Options',
                      addText: 'Add Option',
                      hideRepeaterLabel: true,
                      required: false,
                      titleFieldName: 'label',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) =>
                        !OPTION_BACKED_COLUMN_FIELD_TYPES.has(
                          columnTypeOf(field) ?? ''
                        ),
                    },
                    fieldArray: {
                      fieldGroup: [
                        {
                          key: 'label',
                          type: EIremboFormlyFieldTypes.input,
                          props: {
                            label: 'Option Label',
                            required: true,
                          },
                        },
                        {
                          key: 'value',
                          type: EIremboFormlyFieldTypes.input,
                          props: {
                            label: 'Option Value',
                            required: true,
                          },
                        },
                      ],
                    },
                  },
                  ...getTypeSpecificColumnSettings(),
                  ...getAdvancedColumnSettings(formBuilderService),
                  ...getBespokeColumnSettings(formBuilderService),
                ],
              },
            },
          ],
        },
        {
          key: 'minItems',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: 1,
          props: {
            type: 'number',
            label: 'Minimum Number Of Items',
            min: 0,
            disabled: true,
          },
        },
        {
          key: 'maxItems',
          type: EIremboFormlyFieldTypes.input,
          props: {
            type: 'number',
            label: 'Maximum Number Of Items',
            min: 1,
          },
        },
        {
          key: 'enableEdit',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Allow Editing Rows',
            formCheck: 'switch',
          },
        },
        {
          key: 'enableDeletion',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Allow Deleting Rows',
            formCheck: 'switch',
          },
        },
        {
          key: 'enableRowSelection',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Enable Row Selection',
            formCheck: 'switch',
          },
        },
        {
          key: 'showSearch',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Show Search',
            formCheck: 'switch',
          },
        },
        {
          key: 'showPagination',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Show Pagination',
            formCheck: 'switch',
          },
        },
        {
          key: 'itemsPerPage',
          type: EIremboFormlyFieldTypes.input,
          props: {
            type: 'number',
            label: 'Items Per Page',
            min: 1,
          },
          expressions: {
            hide: '!model.showPagination',
          },
        },
        {
          key: 'uniqueFieldsCombination',
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Composite Uniqueness',
          },
          fieldGroup: [
            {
              key: 'enabled',
              type: EIremboFormlyFieldTypes.checkbox,
              defaultValue: false,
              props: {
                label:
                  'Require A Combination Of Fields To Be Unique Across Rows',
                formCheck: 'switch',
              },
            },
            {
              key: 'firstField',
              type: EIremboFormlyFieldTypes.customdropdown,
              props: {
                label: 'First Field',
                required: true,
                bindLabel: 'label',
                bindValue: 'value',
                options: [],
              },
              expressions: {
                hide: '!model.enabled',
                'props.options': (field: FormlyFieldConfig) =>
                  getColumnFieldOptions(field),
              },
            },
            {
              key: 'secondField',
              type: EIremboFormlyFieldTypes.customdropdown,
              props: {
                label: 'Second Field',
                required: true,
                bindLabel: 'label',
                bindValue: 'value',
                options: [],
              },
              expressions: {
                hide: '!model.enabled',
                'props.options': (field: FormlyFieldConfig) =>
                  getColumnFieldOptions(field),
              },
            },
            {
              key: 'message',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Uniqueness Error Message',
                description: 'e.g., This combination already exists',
              },
              expressions: {
                hide: '!model.enabled',
              },
            },
          ],
        },
      ],
    },
    validations: {
      formModel: {
        required: false,
      },
      formlyForm: [
        {
          key: 'validation',
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Validation',
          },
          fieldGroup: [
            {
              key: 'required',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Required',
                formCheck: 'switch',
              },
            },
          ],
        },
      ],
    },
    display: {
      formModel: {},
      formlyForm: [],
    },
  };
};

// The settings form edits each column as a flat object (label, key, type,
// required, unique, hiddenColumn, widthPercent, options, multiple), but the
// runtime table-with-input field (custom-table-with-input) only ever reads
// column settings nested under `column.props` (TableWithInputColumn). These
// two helpers keep that shape mismatch confined to this file: one nests the
// flat form values on save, the other flattens them back out for editing.
// An empty string/array is what the settings form leaves behind when a
// setting is cleared, so it means "unset" rather than a value to persist.
const isUnsetSettingValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && !value.length);

// A branch holds exactly the settings of the type it is named after, so it
// can be moved in and out of `props` wholesale. Asking each configuration
// which prop names it owns would mean building all of them here, outside any
// form — which is both wasteful and fragile, since they reach into the form
// builder while they build.
const pickTypeSpecificProps = (
  column: ITableWithInputColumnFormModel
): Record<string, unknown> => {
  const flat = column as unknown as Record<string, unknown>;
  const props: Record<string, unknown> = {};

  TYPE_SPECIFIC_COLUMN_PROP_KEYS.forEach(key => {
    if (!isUnsetSettingValue(flat[key])) {
      props[key] = flat[key];
    }
  });

  // Only the branch belonging to the type this column actually is: a branch
  // left behind by a type it used to be must not contribute anything.
  const branch = flat[column.type] as Record<string, unknown> | undefined;
  if (branch && typeof branch === 'object') {
    Object.entries(branch).forEach(([key, value]) => {
      if (!isUnsetSettingValue(value) && !COLUMN_OWNED_PROP_KEYS.has(key)) {
        props[key] = value;
      }
    });
  }

  return props;
};

const readTypeSpecificProps = (
  column: Partial<TableWithInputColumn>,
  branchedTypes: ReadonlySet<string>
): Record<string, unknown> => {
  const props = (column.props ?? {}) as Record<string, unknown>;
  const model: Record<string, unknown> = {};

  TYPE_SPECIFIC_COLUMN_PROP_KEYS.forEach(key => {
    if (props[key] !== undefined) {
      model[key] = props[key];
    }
  });

  // Everything the column doesn't own itself goes back to the type's branch;
  // its fields pick out the ones they know, and anything else is carried
  // through untouched rather than dropped on the next save.
  const type = column.type ?? '';
  if (branchedTypes.has(type)) {
    const branch: Record<string, unknown> = {};
    Object.entries(props).forEach(([key, value]) => {
      if (
        !COLUMN_OWNED_PROP_KEYS.has(key) &&
        !TYPE_SPECIFIC_COLUMN_PROP_KEYS.includes(
          key as (typeof TYPE_SPECIFIC_COLUMN_PROP_KEYS)[number]
        )
      ) {
        branch[key] = value;
      }
    });
    model[type] = branch;
  }

  return model;
};

const mapColumnFormModelToTableWithInputColumn = (
  column: ITableWithInputColumnFormModel
): TableWithInputColumn => {
  // 'number' and 'email' aren't real formly types — the runtime resolves
  // them from type: 'input' + props.type (see resolveTableWithInputColumnKind
  // in irembo-table-with-input.model.ts).
  const inputVariant =
    column.type === 'number' || column.type === 'email' ? column.type : null;
  // The published library still declares TableWithInputColumnFieldType as
  // the original four types until ui-common's own widening ships (see the
  // dependency noted in the PR); the runtime table accepts every registered
  // type. The cast is for the compiler only — what is saved is unchanged.
  const type = (
    column.type === 'number' || column.type === 'email' ? 'input' : column.type
  ) as TableWithInputColumn['type'];
  const messages = columnValidationMessages(column);
  return {
    key: column.key,
    type,
    props: {
      label: column.label,
      ...(inputVariant && { type: inputVariant }),
      required: column.required ?? false,
      unique: column.unique ?? false,
      hiddenColumn: column.hiddenColumn ?? false,
      widthPercent: column.widthPercent ?? null,
      multiple: column.multiple ?? false,
      options: column.options ?? [],
      // Type-specific settings, written under the same names the rendered
      // field reads. Only the ones actually set are emitted, so a column
      // never carries empty props for settings its type doesn't use.
      ...pickTypeSpecificProps(column),
    },
    ...(Object.keys(messages).length > 0 && { validation: { messages } }),
  };
};

// Every validation message the column carries, keyed by the error name the
// runtime row panel reads. The repeater's entries are written first so the
// dedicated Required and Uniqueness fields stay the single source of truth
// for those two: an entry naming `required` or `unique` cannot shadow them.
// An entry missing either half contributes nothing.
const columnValidationMessages = (
  column: ITableWithInputColumnFormModel
): Record<string, string> => {
  const messages: Record<string, string> = {};
  (column.validationMessages ?? []).forEach(({ errorName, errorMessage }) => {
    const name = errorName?.trim();
    if (name && errorMessage) {
      messages[name] = errorMessage;
    }
  });
  if (column.errorMessage) {
    messages['required'] = column.errorMessage;
  }
  if (column.uniquenessErrorMessage) {
    messages['unique'] = column.uniquenessErrorMessage;
  }
  return messages;
};

// The saved messages, back as repeater entries. Required and unique are left
// out: they are read into their own fields above, and carrying them here too
// would show the same wording twice. Every other error is surfaced whatever
// its name, including ones this wizard has no knowledge of.
const readColumnErrorMessages = (
  column: Partial<TableWithInputColumn>
): ITableWithInputColumnValidationMessage[] =>
  Object.entries(column.validation?.messages ?? {})
    .filter(([errorName]) => errorName !== 'required' && errorName !== 'unique')
    .map(([errorName, errorMessage]) => ({
      errorName,
      errorMessage: String(errorMessage ?? ''),
    }));

// Which types keep their settings in a branch. Reading this needs no live
// form — only the names — so the type list is derived once from the two
// registries rather than by building every configuration.
const BRANCHED_COLUMN_TYPES: ReadonlySet<string> = new Set([
  ...ADVANCED_FORM_FIELD_TYPES.filter(
    type => !EXCLUDED_COLUMN_FIELD_TYPES.has(type)
  ),
  ...Object.keys(BESPOKE_COLUMN_TYPE_CONFIGS),
]);

const mapTableWithInputColumnToColumnFormModel = (
  // Legacy columns saved before this fix stored these fields flat instead of
  // nested under `props` — fall back to the flat reads so those don't appear
  // blank when reopened.
  column: Partial<TableWithInputColumn> &
    Partial<ITableWithInputColumnFormModel>
): ITableWithInputColumnFormModel => ({
  label: column.props?.label ?? column.label ?? '',
  key: column.key ?? '',
  type:
    column.type === 'input' &&
    (column.props?.type === 'number' || column.props?.type === 'email')
      ? column.props.type
      : (column.type as ITableWithInputColumnFormModel['type'] | undefined) ??
        'input',
  required: column.props?.required ?? column.required ?? false,
  errorMessage: column.validation?.messages?.required ?? '',
  unique: column.props?.unique ?? column.unique ?? false,
  uniquenessErrorMessage: column.validation?.messages?.unique ?? '',
  validationMessages: readColumnErrorMessages(column),
  hiddenColumn: column.props?.hiddenColumn ?? column.hiddenColumn ?? false,
  widthPercent: column.props?.widthPercent ?? column.widthPercent ?? null,
  multiple: column.props?.multiple ?? column.multiple ?? false,
  options:
    (column.props?.options as { label: string; value: string }[] | undefined) ??
    column.options ??
    [],
  ...readTypeSpecificProps(column, BRANCHED_COLUMN_TYPES),
});

// TableWithInputUniqueFieldsCombination stores the pair as a `fields` tuple;
// the settings form edits them as two separate dropdowns instead, so this
// (and its mirror below) keep that shape mismatch confined to this file, the
// same way mapColumnFormModelToTableWithInputColumn/
// mapTableWithInputColumnToColumnFormModel do for columns.
const mapUniqueFieldsCombinationConfigToFormModel = (
  config:
    | {
        enabled?: boolean;
        fields?: [string, string];
        message?: string;
      }
    | undefined
): ITableWithInputUniqueFieldsCombinationFormModel => ({
  enabled: config?.enabled ?? false,
  firstField: config?.fields?.[0] ?? null,
  secondField: config?.fields?.[1] ?? null,
  message: config?.message ?? null,
});

const mapUniqueFieldsCombinationFormModelToConfig = (
  model: ITableWithInputUniqueFieldsCombinationFormModel | undefined
):
  | {
      enabled: boolean;
      fields: [string, string];
      message?: string;
    }
  | undefined => {
  if (!model?.enabled || !model.firstField || !model.secondField) {
    return undefined;
  }
  return {
    enabled: true,
    fields: [model.firstField, model.secondField],
    ...(model.message && { message: model.message }),
  };
};

export const tableWithInputFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): ITableWithInputConfigurationFormModel => {
  return {
    ...defaultTableWithInputConfigFormModel,
    fieldKey: (field.key as string) ?? '',
    fieldLabel: field.props?.label ?? '',
    placeholder: field.props?.placeholder ?? '',
    hint: field.props?.['hint'] ?? '',
    tooltip: field.props?.['tooltip'] || '',
    internalDescription: field.props?.['internalDescription'] ?? '',
    columns: (field.props?.['columns'] || []).map(
      mapTableWithInputColumnToColumnFormModel
    ),
    minItems: field.props?.['minItems'] ?? 1,
    maxItems: field.props?.['maxItems'] ?? null,
    enableEdit: field.props?.['enableEdit'] ?? true,
    enableDeletion: field.props?.['enableDeletion'] ?? true,
    enableRowSelection: field.props?.['enableRowSelection'] ?? false,
    showSearch: field.props?.['showSearch'] ?? false,
    showPagination: field.props?.['showPagination'] ?? true,
    itemsPerPage: field.props?.['itemsPerPage'] ?? 10,
    uniqueFieldsCombination: mapUniqueFieldsCombinationConfigToFormModel(
      field.props?.['uniqueFieldsCombination']
    ),
  };
};

export const tableWithInputFieldUpdateFromSettingsFormModel = (
  model: ITableWithInputConfigurationFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field.props = field.props || {};

  field.props['label'] = model.fieldLabel || undefined;
  field.props['placeholder'] = model.placeholder || undefined;
  field.props['hint'] = model.hint;
  field.props['tooltip'] = model.tooltip;
  field.props['internalDescription'] = model.internalDescription;

  field.props['columns'] = model.columns.map(
    mapColumnFormModelToTableWithInputColumn
  );
  field.props['minItems'] = model.minItems ?? undefined;
  field.props['maxItems'] = model.maxItems ?? undefined;
  field.props['enableEdit'] = model.enableEdit;
  field.props['enableDeletion'] = model.enableDeletion;
  field.props['enableRowSelection'] = model.enableRowSelection;
  field.props['showSearch'] = model.showSearch;
  field.props['showPagination'] = model.showPagination;
  field.props['itemsPerPage'] = model.itemsPerPage;
  field.props['uniqueFieldsCombination'] =
    mapUniqueFieldsCombinationFormModelToConfig(model.uniqueFieldsCombination);

  return field;
};

export const tableWithInputFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  return parseRequiredValidatorParseFieldIntoModel({}, field);
};

export const tableWithInputFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setRequiredValidatorUpdateFieldFromModel(model, field);
};

export const tableWithInputFieldParseIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const tableWithInputFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};
